import { Suspense, useContext, useEffect, useRef, memo, useState } from 'react';
import GalleryContext from '@/src/Gallery/components/GalleryContext';
import { ErrorBoundary } from 'react-error-boundary';
import placeholder from './placeholder.jpg';
import GalleryShareContext from './GalleryShareContext';
import VideoPlayer from './VideoPlayer';
import usePhotoViewLogger from '@/src/Stats/components/hooks/usePhotoViewLogger';
import { logJourneyEvent } from '@/src/Stats/components/hooks/useShareJourney';
import DownloadButtonV2 from '@/src/Share/components/DownloadButtonV2';
import useRekognition, {
    RekognitionDetectedFace,
    RekognitionLabel,
} from '@/src/Activations/components/hooks/useRekognition';
import useMediaTags, {
    MediaTag,
} from '@/src/Activations/components/hooks/useMediaTags';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';

const VIDEO_MIME_TYPE = 'video/mp4';

function PlaceholderImage() {
    return <img src={placeholder} alt="" />;
}

interface ErrorFallbackProps {
    error: any;
    resetErrorBoundary: any;
}

function ErrorFallback({ resetErrorBoundary }: ErrorFallbackProps) {
    useEffect(() => {
        setTimeout(resetErrorBoundary, 100);
    });

    return <PlaceholderImage />;
}

export const GifWrapper = memo(({ url }: { url: string }) => {
    return (
        <div className="Gif-frame">
            <img src={url} alt="" draggable={false} />
            <div className="button-container">
                <i
                    className="bi bi-play"
                    style={{ filter: 'drop-shadow(0px 0px 5px #222)' }}
                />
            </div>
        </div>
    );
});

function ImageWrapper({ media, isExpanded }: GalleryPreviewImageProps) {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [uri, setUri] = useState<string>(
        media.thumbnailTinyUri || media.thumbnail_800pxUri || media.originalUri,
    );

    useEffect(() => {
        const image = new Image();
        image.src = media.originalUri;
        image.onload = () => {
            setUri(media.originalUri);
            setIsLoading(false);
        };

        return () => {
            image.onload = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uri, media]);

    return (
        <div className="relative w-full h-full">
            {isLoading && <div className="loader absolute " />}

            <img
                src={uri}
                loading="lazy"
                alt=""
                draggable={false}
                className={`rounded-[0px] w-full h-full object-cover ${
                    isLoading ? '' : ''
                }`}
            />
        </div>
    );
}

export function MediaWrapper(props: GalleryPreviewImageProps) {
    const { media, isExpanded } = props;
    const { originalUri, thumbnailUri, mimeType } = media;

    let component = null;
    if (mimeType === VIDEO_MIME_TYPE && isExpanded) {
        component = <VideoPlayer url={originalUri} showControls={false} />;
    } else if (mimeType === VIDEO_MIME_TYPE) {
        component = <GifWrapper url={thumbnailUri} />;
    } else {
        component = <ImageWrapper {...props} key={media.id} />;
    }

    return (
        <div className="Media-container">
            {component}
            <div className="click-blocker" />
        </div>
    );
}

interface GalleryPreviewImageProps {
    media: MediaInstance;
    isExpanded?: boolean;
    galleryInfo?: any;
    index?: number;
    setExpandedIndex?: (index: any) => void;
}

function VisibleImage(props: GalleryPreviewImageProps) {
    const delayed = useRef(false);

    useEffect(() => {
        setTimeout(() => (delayed.current = true), 100);
    }, []);

    return <MediaWrapper {...props} />;
}

export default function GalleryLightbox({
    photos,
    currentPhotoIndex,
    gestureHandlers,
    showLabels = false,
    showFaces = false,
    showHideButton = false,
    showTags = false,
}: {
    photos: MediaInstance[];
    currentPhotoIndex: number;
    gestureHandlers: any;
    showLabels?: boolean;
    showFaces?: boolean;
    showHideButton?: boolean;
    showTags?: boolean;
}) {
    const { shareIdentifier, setShareIdentifier, baseUrl, slug } =
        useContext(GalleryContext);
    const galleryShareContext = useContext(GalleryShareContext);
    const [detectedFaces, setDetectedFaces] = useState<
        RekognitionDetectedFace[]
    >([]);
    const [detectedLabels, setDetectedLabels] = useState<RekognitionLabel[]>(
        [],
    );
    const [pendingLabel, setPendingLabel] = useState<string>('');
    const [isSavingLabel, setIsSavingLabel] = useState<boolean>(false);
    const [labelError, setLabelError] = useState<string | null>(null);
    const [mediaTags, setMediaTags] = useState<MediaTag[]>([]);
    const [pendingTag, setPendingTag] = useState<string>('');
    const [isSavingTag, setIsSavingTag] = useState<boolean>(false);
    const [tagError, setTagError] = useState<string | null>(null);

    const {
        getDetectedFaces,
        getDetectedLabels,
        addManualLabel,
        removeLabel,
    } = useRekognition();

    const { getTagsForMedia, addTags, removeTags } = useMediaTags();

    const authenticatedApi = useAuthenticatedApi();

    const photo = photos.find((p) => p.shareIdentifier === shareIdentifier);
    useEffect(() => {
        setDetectedFaces([]);
        setDetectedLabels([]);
        setPendingLabel('');
        setLabelError(null);
        setMediaTags([]);
        setPendingTag('');
        setTagError(null);
        let isMounted = true;
        if (photo) {
            if (showFaces) {
                getDetectedFaces(photo.id).then((response) => {
                    if (isMounted) {
                        setDetectedFaces(response.data);
                    }
                });
            }
            if (showLabels) {
                getDetectedLabels(photo.id).then((response) => {
                    if (isMounted) {
                        setDetectedLabels(response.data);
                    }
                });
            }
            if (showTags) {
                getTagsForMedia(photo.id).then((response) => {
                    if (isMounted) {
                        setMediaTags(response.data);
                    }
                });
            }
        }
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photo?.id]);

    const { logPhotoView } = usePhotoViewLogger();
    // Log each distinct photo the guest views, including paging within the
    // lightbox (previously only the first photo per mount was logged).
    const lastViewedId = useRef<number | null>(null);

    useEffect(() => {
        if (photo && lastViewedId.current !== photo.id) {
            lastViewedId.current = photo.id;
            logPhotoView(photo.activationId, photo.id);
            logJourneyEvent('media-view', { mediaId: photo.id });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photo]);

    const toggleHidden = async (
        mediaId: number,
        isHidden: boolean,
    ): Promise<boolean> => {
        const toggleVisibility = async () => {
            await authenticatedApi.put(`media/${mediaId}/visibility`, {
                isHidden: !isHidden,
            });

            return !isHidden;
        };

        return await toggleVisibility();
    };

    const [isHidden, setLocalIsHidden] = useState<boolean>(
        photo?.isHidden || false,
    );

    if (!photo) {
        return null;
    }

    const { mimeType } = photo;

    const isImage =
        mimeType?.toLowerCase() === 'image/jpeg' ||
        mimeType?.toLowerCase() === 'image/jpg';

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback} onReset={() => {}}>
            <Suspense>
                <div className="expanded-view" {...gestureHandlers}>
                    <div
                        className={`AbsoluteGallery-cell expanded${
                            isImage ? ' image' : ''
                        }`}
                    >
                        <div className="absolute top-0 left-0 w-full h-[80px] z-[1000]">
                            <div
                                className={`visible md:invisible options flex flex-row justify-center items-center gap-2 ${
                                    isImage ? '' : 'video'
                                } z-100`}
                            >
                                <DownloadButtonV2
                                    uri={photo.originalUri}
                                    mimeType={photo.mimeType}
                                    mediaId={photo.id}
                                    source="gallery"
                                />

                                {galleryShareContext && (
                                    <i
                                        onClick={() => {
                                            galleryShareContext.setShowShareModal(
                                                photo.shareIdentifier,
                                            );
                                        }}
                                        className="bi bi-send save-button"
                                    ></i>
                                )}
                                <div className="spacer" />
                                <div className="text-sm">
                                    <b>{currentPhotoIndex + 1}&nbsp;</b> /&nbsp;
                                    {photos.length}
                                </div>
                                <div className="spacer" />
                                <div
                                    className="text-[28px]"
                                    onClick={() => {
                                        setShareIdentifier(null);
                                    }}
                                >
                                    <i className="bi bi-x text-[28px] w-[28px] h-[28px]"></i>
                                </div>
                            </div>

                            <div className="invisible md:visible">
                                <div className="spacer" />
                                <div
                                    className="Close-lightbox"
                                    onClick={() => {
                                        setShareIdentifier(null);
                                    }}
                                >
                                    <i className="bi bi-x w-[28px] h-[28px]"></i>
                                </div>
                            </div>
                        </div>

                        <div
                            className={`mt-20 md:mt-0 ${
                                isImage ? '' : 'pt-[30px]'
                            }`}
                        >
                            <VisibleImage
                                media={photo}
                                isExpanded={true}
                                key={photo.shareIdentifier}
                            />
                        </div>

                        <div
                            className={`invisible absolute text-[22px] bottom-5 w-full md:visible flex flex-row justify-center items-center gap-2 ${
                                isImage ? '' : 'video'
                            } z-1000`}
                        >
                            {showHideButton && (
                                <>
                                    <div className="spacer" />
                                    <div
                                        onClick={async (event: any) => {
                                            event.stopPropagation();
                                            event.preventDefault();

                                            const newState = await toggleHidden(
                                                photo.id,
                                                isHidden,
                                            );

                                            setLocalIsHidden(newState);
                                        }}
                                    >
                                        <i
                                            className={`bi ${
                                                isHidden
                                                    ? 'bi-eye-slash'
                                                    : 'bi-eye'
                                            } clickable`}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="spacer" />
                            <DownloadButtonV2
                                uri={photo.originalUri}
                                mimeType={photo.mimeType}
                                mediaId={photo.id}
                                source="gallery"
                            />

                            {galleryShareContext && (
                                <i
                                    onClick={() => {
                                        galleryShareContext.setShowShareModal(
                                            photo.shareIdentifier,
                                        );
                                    }}
                                    className="bi bi-send save-button ml-2"
                                ></i>
                            )}
                            <div className="spacer" />
                        </div>
                        {/*
                         * Faces / Labels panel.
                         *
                         * The expanded photo has `z-index: 99` (see Gallery.css
                         * `.AbsoluteGallery-cell.expanded img`), so this panel
                         * needs a higher stacking order or it paints behind
                         * the photo and is invisible. We also constrain it to
                         * the top-left corner instead of covering the full
                         * cell so it doesn't intercept clicks meant for the
                         * photo or the swipe gesture handlers. The
                         * semi-opaque dark backdrop keeps white text/chips
                         * legible on top of bright photos. Top offset stays
                         * at 80px on mobile so it clears the mobile top
                         * toolbar (h-[80px]); desktop hides that toolbar so
                         * we can sit much higher.
                         */}
                        {(detectedFaces.length > 0 ||
                            ((showLabels || showTags) && photo)) && (
                            <div className="absolute top-[80px] md:top-3 left-2 z-[100] max-w-[400px] flex flex-col gap-3 bg-black/60 backdrop-blur-sm rounded-lg p-3 shadow-lg text-white">
                                {detectedFaces.length > 0 && (
                                <>
                                    <div className="text-sm font-bold underline">
                                        Faces Detected
                                    </div>
                                    <div className="flex flex-row gap-2">
                                        {detectedFaces.map((face) => (
                                            <img
                                                src={face.faceCatalogUrl}
                                                alt=""
                                                className="cursor-pointer w-[75px] h-[75px] object-cover rounded-full border-2 border-white"
                                                onClick={async () => {
                                                    window.open(
                                                        `${baseUrl}/face-query/${encodeURIComponent(
                                                            slug ?? '',
                                                        )}?faceIds=${
                                                            face.faceVector
                                                        }`,
                                                    );
                                                }}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                            {showLabels && photo && (
                                <LabelEditor
                                    labels={detectedLabels}
                                    pendingLabel={pendingLabel}
                                    onPendingLabelChange={setPendingLabel}
                                    isSaving={isSavingLabel}
                                    error={labelError}
                                    onAdd={async () => {
                                        const trimmed = pendingLabel.trim();
                                        if (!trimmed || isSavingLabel) return;
                                        setLabelError(null);
                                        setIsSavingLabel(true);
                                        try {
                                            const { data } =
                                                await addManualLabel(
                                                    photo.id,
                                                    trimmed,
                                                );
                                            setDetectedLabels((prev) =>
                                                prev.some(
                                                    (l) => l.id === data.id,
                                                )
                                                    ? prev
                                                    : [...prev, data],
                                            );
                                            setPendingLabel('');
                                        } catch (e: any) {
                                            setLabelError(
                                                e?.response?.data?.message ||
                                                    e?.message ||
                                                    'Failed to add label',
                                            );
                                        } finally {
                                            setIsSavingLabel(false);
                                        }
                                    }}
                                    onRemove={async (label) => {
                                        const previous = detectedLabels;
                                        setDetectedLabels((prev) =>
                                            prev.filter((l) => l.id !== label.id),
                                        );
                                        try {
                                            await removeLabel(label.id);
                                        } catch (e: any) {
                                            setDetectedLabels(previous);
                                            setLabelError(
                                                e?.response?.data?.message ||
                                                    e?.message ||
                                                    'Failed to remove label',
                                            );
                                        }
                                    }}
                                />
                            )}
                            {showTags && photo && (
                                <TagEditor
                                    tags={mediaTags}
                                    pendingTag={pendingTag}
                                    onPendingTagChange={setPendingTag}
                                    isSaving={isSavingTag}
                                    error={tagError}
                                    onAdd={async () => {
                                        const trimmed = pendingTag.trim();
                                        if (!trimmed || isSavingTag) return;
                                        setTagError(null);
                                        setIsSavingTag(true);
                                        try {
                                            const { data } = await addTags(
                                                photo.id,
                                                [trimmed],
                                            );
                                            setMediaTags(data);
                                            setPendingTag('');
                                        } catch (e: any) {
                                            setTagError(
                                                e?.response?.data?.message ||
                                                    e?.message ||
                                                    'Failed to add tag',
                                            );
                                        } finally {
                                            setIsSavingTag(false);
                                        }
                                    }}
                                    onRemove={async (tag) => {
                                        const previous = mediaTags;
                                        setMediaTags((prev) =>
                                            prev.filter((t) => t.id !== tag.id),
                                        );
                                        try {
                                            await removeTags(photo.id, [
                                                tag.tag,
                                            ]);
                                        } catch (e: any) {
                                            setMediaTags(previous);
                                            setTagError(
                                                e?.response?.data?.message ||
                                                    e?.message ||
                                                    'Failed to remove tag',
                                            );
                                        }
                                    }}
                                />
                            )}
                            </div>
                        )}
                        <div className="absolute invisible md:visible bottom-5 right-5 w-[200px] flex justify-end items-end">
                            <b>{currentPhotoIndex + 1}&nbsp;</b> /&nbsp;
                            {photos.length}
                        </div>
                    </div>
                </div>
            </Suspense>
        </ErrorBoundary>
    );
}

/**
 * Inline label editor for the authenticated web media manager.
 *
 * Renders existing labels (from media_rekognition_label_xref) as chips and
 * lets the user add manual labels or remove ones they previously added.
 * Auto-detected Rekognition labels are shown but cannot be removed here
 * (the server-side endpoint refuses non-manual deletes).
 */
function LabelEditor({
    labels,
    pendingLabel,
    onPendingLabelChange,
    isSaving,
    error,
    onAdd,
    onRemove,
}: {
    labels: RekognitionLabel[];
    pendingLabel: string;
    onPendingLabelChange: (value: string) => void;
    isSaving: boolean;
    error: string | null;
    onAdd: () => void | Promise<void>;
    onRemove: (label: RekognitionLabel) => void | Promise<void>;
}) {
    return (
        <div className="text-white">
            <div className="text-sm font-bold underline">Labels</div>
            <div className="flex flex-wrap gap-1.5 mt-1">
                {labels.length === 0 && (
                    <div className="text-xs italic opacity-70">
                        No labels yet
                    </div>
                )}
                {labels.map((label) => {
                    const isManual = label.metadata?.source === 'manual';
                    const removeAriaLabel = isManual
                        ? `Remove label ${label.label}`
                        : `Remove auto-detected label ${label.label}`;
                    return (
                        <span
                            key={label.id}
                            title={
                                isManual
                                    ? 'Added manually'
                                    : 'Detected by Rekognition — remove to correct false positives'
                            }
                            className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 ${
                                isManual ? 'bg-blue-500/80' : 'bg-white/20'
                            }`}
                        >
                            {label.label}
                            <button
                                type="button"
                                aria-label={removeAriaLabel}
                                className="ml-0.5 leading-none opacity-80 hover:opacity-100"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    onRemove(label);
                                }}
                            >
                                ×
                            </button>
                        </span>
                    );
                })}
            </div>
            <form
                className="mt-2 flex items-center gap-1"
                onSubmit={(e) => {
                    e.preventDefault();
                    onAdd();
                }}
            >
                <input
                    type="text"
                    value={pendingLabel}
                    onChange={(e) => onPendingLabelChange(e.target.value)}
                    placeholder="Add label…"
                    disabled={isSaving}
                    className="text-xs px-2 py-1 rounded bg-white/10 text-white placeholder-white/60 focus:outline-none focus:bg-white/20 disabled:opacity-50"
                />
                <button
                    type="submit"
                    disabled={isSaving || !pendingLabel.trim()}
                    className="text-xs px-2 py-1 rounded bg-white/20 hover:bg-white/30 disabled:opacity-50"
                >
                    {isSaving ? 'Adding…' : 'Add'}
                </button>
            </form>
            {error && (
                <div className="text-xs text-red-300 mt-1" role="alert">
                    {error}
                </div>
            )}
        </div>
    );
}

/**
 * Inline per-image tag editor for the authenticated web media manager.
 *
 * Renders the image's tags (media_tag) as chips and lets operators add or
 * remove them — including fixing booth-applied tags or pulling an image out
 * of a tag-filtered slideshow. The chip color hints at where a tag came
 * from: booth-applied (capture stage or setTag action) vs added here.
 */
function TagEditor({
    tags,
    pendingTag,
    onPendingTagChange,
    isSaving,
    error,
    onAdd,
    onRemove,
}: {
    tags: MediaTag[];
    pendingTag: string;
    onPendingTagChange: (value: string) => void;
    isSaving: boolean;
    error: string | null;
    onAdd: () => void | Promise<void>;
    onRemove: (tag: MediaTag) => void | Promise<void>;
}) {
    return (
        <div className="text-white">
            <div className="text-sm font-bold underline">Tags</div>
            <div className="flex flex-wrap gap-1.5 mt-1">
                {tags.length === 0 && (
                    <div className="text-xs italic opacity-70">No tags yet</div>
                )}
                {tags.map((tag) => {
                    const fromBooth =
                        tag.source === 'capture' || tag.source === 'action';
                    return (
                        <span
                            key={tag.id}
                            title={
                                fromBooth
                                    ? `Applied by the booth (${tag.source})`
                                    : 'Added in the media manager'
                            }
                            className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 ${
                                fromBooth ? 'bg-emerald-600/80' : 'bg-blue-500/80'
                            }`}
                        >
                            {tag.tag}
                            <button
                                type="button"
                                aria-label={`Remove tag ${tag.tag}`}
                                className="ml-0.5 leading-none opacity-80 hover:opacity-100"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    onRemove(tag);
                                }}
                            >
                                ×
                            </button>
                        </span>
                    );
                })}
            </div>
            <form
                className="mt-2 flex items-center gap-1"
                onSubmit={(e) => {
                    e.preventDefault();
                    onAdd();
                }}
            >
                <input
                    type="text"
                    value={pendingTag}
                    onChange={(e) => onPendingTagChange(e.target.value)}
                    placeholder="Add tag…"
                    disabled={isSaving}
                    className="text-xs px-2 py-1 rounded bg-white/10 text-white placeholder-white/60 focus:outline-none focus:bg-white/20 disabled:opacity-50"
                />
                <button
                    type="submit"
                    disabled={isSaving || !pendingTag.trim()}
                    className="text-xs px-2 py-1 rounded bg-white/20 hover:bg-white/30 disabled:opacity-50"
                >
                    {isSaving ? 'Adding…' : 'Add'}
                </button>
            </form>
            {error && (
                <div className="text-xs text-red-300 mt-1" role="alert">
                    {error}
                </div>
            )}
        </div>
    );
}
